import { Badge } from "react-bootstrap";
import { formatCurrency } from "../lib/numbers";
import { formatSignedCurrency } from "./CashOverrideModal";

export interface CashBalanceOverrideBadgeProps {
  /** The signed adjustment added to the reported cash. */
  adjustment: number;
  /** The cash the trade proposal ran with. Can be negative. */
  amount: number;
  /** The cash the custodian reported when the adjustment was made. */
  reportedCashBalance: number | null;
  /** Who adjusted it, when known. */
  username?: string | null;
  reason?: string | null;
}

/** `$29,868`, or `−$1,200` for negative cash. */
const formatCash = (value: number) =>
  `${value < 0 ? "−" : ""}${formatCurrency(Math.abs(value))}`;

/**
 * The intraday cash adjustment a trade proposal ran with for one account:
 * "Cash adjusted +$20,000 ($9,868 → $29,868) by jordan@example.com ·
 * Deposit landed". Shown on the review page (`RebalanceAccountInsights`) and
 * the explain page's cash section.
 */
export default function CashBalanceOverrideBadge({
  adjustment,
  amount,
  reportedCashBalance,
  username,
  reason,
}: CashBalanceOverrideBadgeProps) {
  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2 small"
      data-testid="cash-balance-override"
    >
      <Badge bg={adjustment < 0 ? "warning" : "info"} text="dark">
        Cash adjusted
      </Badge>{" "}
      <span>
        <strong>{formatSignedCurrency(adjustment)}</strong> (
        {reportedCashBalance === null
          ? `none reported → ${formatCash(amount)}`
          : `${formatCash(reportedCashBalance)} → ${formatCash(amount)}`}
        ){username ? <> by {username}</> : null}
        {reason ? <span className="text-muted"> · {reason}</span> : null}
      </span>
    </div>
  );
}
